export interface RoleAwareUser {
  isAdmin?: boolean | null;
  adminRoles?: string[] | null;
  adminPermissions?: string[] | null;
}

export const ADMIN_CONTROL = {
  path: "/admin",
  label: "Admin Control Center",
} as const;

export const ADMIN_SECTIONS = [
  { slug: "", label: "Overview", path: "/admin", permissions: ["admin.reports.read"] },
  { slug: "analytics", label: "Feature analytics", path: "/admin/analytics", permissions: ["admin.analytics.read"] },
  { slug: "applications", label: "Astrologer applications", path: "/admin/applications", permissions: ["admin.applications.read"] },
  { slug: "payouts", label: "Payouts", path: "/admin/payouts", permissions: ["admin.payouts.read"] },
  { slug: "abuse", label: "Abuse controls", path: "/admin/abuse", permissions: ["admin.abuse.read"] },
  { slug: "bookings", label: "Bookings", path: "/admin/bookings", permissions: ["admin.bookings.read"] },
  { slug: "users", label: "Users", path: "/admin/users", permissions: ["admin.users.read"] },
  { slug: "payments", label: "Payments & refunds", path: "/admin/payments", permissions: ["admin.payments.read"] },
  { slug: "notifications", label: "Broadcasts", path: "/admin/notifications", permissions: ["admin.notifications.broadcast"] },
  { slug: "feedback", label: "In-app feedback", path: "/admin/feedback", permissions: ["admin.feedback.read"] },
  { slug: "backlinks", label: "Backlink tools", path: "/admin/backlinks", permissions: ["admin.content.backlinks.read"] },
  { slug: "audit", label: "Audit log", path: "/admin/audit", permissions: ["admin.audit.read"] },
  { slug: "vastu", label: "Vastu preview", path: "/admin/vastu", permissions: ["admin.vastu.reports.read"] },
  { slug: "vastu-operations", label: "Vastu reviewers", path: "/admin/vastu-operations", permissions: ["admin.vastu.reviewers.manage", "admin.vastu.reviews.read", "admin.vastu.payouts.mark_paid"] },
  { slug: "access", label: "Admin access", path: "/admin/access", permissions: ["admin.roles.manage"] },
] as const;

export type AdminSection = (typeof ADMIN_SECTIONS)[number];

export const ADMIN_USER_DRILLDOWNS = {
  registered: { label: "Registered users", query: "segment=registered" },
  paid: { label: "Active premium users", query: "segment=paid" },
  new: { label: "New users (last 30 days)", query: "segment=new" },
  guests: { label: "Guest sessions", query: "segment=guests" },
} as const;

export type AdminUserDrilldown = keyof typeof ADMIN_USER_DRILLDOWNS;

export function getAdminUserDrilldown(value: string | null | undefined): AdminUserDrilldown | undefined {
  return value && Object.prototype.hasOwnProperty.call(ADMIN_USER_DRILLDOWNS, value)
    ? (value as AdminUserDrilldown)
    : undefined;
}

export function getAdminUsersDrilldownHref(segment: AdminUserDrilldown): string {
  return `/admin/users?${ADMIN_USER_DRILLDOWNS[segment].query}`;
}

export function getAdminPaymentsDrilldownHref(currency: string): string {
  return `/admin/payments?currency=${encodeURIComponent(currency)}`;
}

export function isAdministrator(user: RoleAwareUser | null | undefined): boolean {
  return Array.isArray(user?.adminRoles) && user.adminRoles.length > 0;
}

export function hasAdminPermission(
  user: RoleAwareUser | null | undefined,
  permission: string,
): boolean {
  return Array.isArray(user?.adminPermissions) && user.adminPermissions.includes(permission);
}

export function canAccessAdminSection(
  user: RoleAwareUser | null | undefined,
  section: AdminSection,
): boolean {
  return section.permissions.some((permission) => hasAdminPermission(user, permission));
}

export function getAdminControl(user: RoleAwareUser | null | undefined) {
  return isAdministrator(user) ? ADMIN_CONTROL : null;
}

export function isAdminRoute(pathname: string): boolean {
  return pathname === ADMIN_CONTROL.path || pathname.startsWith(`${ADMIN_CONTROL.path}/`);
}

export function isActiveAdminSection(pathname: string, section: AdminSection): boolean {
  return pathname.split(/[?#]/, 1)[0] === section.path;
}

export function getAdminSectionByPath(pathname: string): AdminSection | undefined {
  return ADMIN_SECTIONS.find((section) => isActiveAdminSection(pathname, section));
}

export function getAccountRoleLabel(user: RoleAwareUser | null | undefined): string | null {
  if (!isAdministrator(user)) return null;
  const labels: Record<string, string> = {
    platform_owner: "Platform owner",
    user_support: "User support",
    user_safety: "User safety",
    finance_refunds: "Refunds",
    payout_operator: "Payout operations",
    communications: "Communications",
    marketplace_reviewer: "Marketplace review",
    content_operator: "Content operations",
    vastu_operator: "Vastu operations",
    analytics_auditor: "Analytics and audit",
  };
  return user!.adminRoles!.map((role) => labels[role]).filter(Boolean).join(", ") || "Administrator";
}